import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { 
  Home,
  ArrowLeft,
  Phone,
  Search,
  MapPin,
  Briefcase
} from 'lucide-react';

const NotFoundPage = () => {
  const navigate = useNavigate();

  const popularPages = [
    { name: "Home", path: "/", icon: Home },
    { name: "Our Services", path: "/services", icon: Briefcase },
    { name: "Service Areas", path: "/areas", icon: MapPin },
    { name: "Contact Us", path: "/contact", icon: Phone },
    { name: "Why Choose Us", path: "/why-choose-us", icon: Home },
    { name: "Get a Quote", path: "/contact", icon: Phone }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Page Not Found - Pierce Premier",
    "description": "The page you're looking for could not be found. Find Pierce Premier cleaning services, service areas, and contact information."
  };

  return (
    <>
      <SEO 
        title="Page Not Found | Pierce Premier Cleaning Services"
        description="The page you're looking for could not be found. Find Pierce Premier cleaning services, service areas, and contact information."
        canonical="/404"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16">
          
          {/* 404 Hero */}
          <div className="text-center mb-12">
            <div className="inline-block mb-6">
              <div className="w-32 h-32 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full flex items-center justify-center mx-auto">
                <span className="text-white text-6xl font-bold">404</span>
              </div>
            </div>
            
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Page Not Found
            </h1>
            <p className="text-lg text-gray-600 mb-6">
              We couldn't find the page you're looking for. It may have been moved, deleted, or the URL might be mistyped.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate(-1)}
                variant="outline"
                className="inline-flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Go Back
              </Button>
              <Link to="/">
                <Button className="bg-blue-600 hover:bg-blue-700 inline-flex items-center gap-2">
                  <Home className="w-4 h-4" />
                  Go to Homepage
                </Button>
              </Link>
            </div>
            
            {/* Cleaning Quote CTA */}
            <div className="mt-8 p-6 bg-green-50 border-2 border-green-200 rounded-lg">
              <p className="text-lg font-semibold text-gray-900 mb-3 text-center">
                Looking for a cleaning quote?
              </p>
              <div className="flex justify-center">
                <Link to="/contact">
                  <Button className="bg-green-600 hover:bg-green-700 inline-flex items-center gap-2 px-8 py-6 text-base">
                    <Phone className="w-5 h-5" />
                    Request a Free Estimate
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Popular Pages */}
          <Card className="mb-12">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                Popular Pages
              </h2>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                {popularPages.map((page, idx) => {
                  const Icon = page.icon;
                  return (
                    <Link 
                      key={idx}
                      to={page.path}
                      className="flex items-center gap-3 p-4 rounded-lg border-2 border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-colors group"
                    >
                      <Icon className="w-5 h-5 text-blue-600" />
                      <span className="font-semibold text-gray-900 group-hover:text-blue-600">
                        {page.name}
                      </span>
                    </Link>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Services Quick Links */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Residential Services
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/services/house-cleaning" className="text-blue-600 hover:text-blue-700 hover:underline">
                      House Cleaning
                    </Link>
                  </li>
                  <li>
                    <Link to="/services/deep-cleaning" className="text-blue-600 hover:text-blue-700 hover:underline">
                      Deep Cleaning
                    </Link>
                  </li>
                  <li>
                    <Link to="/services/move-in-move-out-cleaning" className="text-blue-600 hover:text-blue-700 hover:underline">
                      Move-In / Move-Out Cleaning
                    </Link>
                  </li>
                  <li>
                    <Link to="/services/vacation-rental-cleaning" className="text-blue-600 hover:text-blue-700 hover:underline">
                      Vacation Rental Cleaning
                    </Link>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  Commercial Services
                </h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/services/commercial/office-cleaning" className="text-blue-600 hover:text-blue-700 hover:underline">
                      Office Cleaning
                    </Link>
                  </li>
                  <li>
                    <Link to="/services/commercial/property-management" className="text-blue-600 hover:text-blue-700 hover:underline">
                      Property Management Cleaning
                    </Link>
                  </li>
                  <li>
                    <Link to="/services/commercial/real-estate-cleaning" className="text-blue-600 hover:text-blue-700 hover:underline">
                      Real Estate Cleaning
                    </Link>
                  </li>
                  <li>
                    <Link to="/services/commercial/construction-cleanup" className="text-blue-600 hover:text-blue-700 hover:underline">
                      Post-Construction Cleaning
                    </Link>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Service Areas */}
          <Card className="mb-12">
            <CardContent className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-600" />
                Service Areas in Brevard County
              </h3>
              <div className="grid sm:grid-cols-3 md:grid-cols-4 gap-3">
                {[
                  "Palm Bay",
                  "Melbourne", 
                  "Viera",
                  "Satellite Beach",
                  "Indialantic",
                  "Cocoa Beach",
                  "Melbourne Beach",
                  "Rockledge",
                  "Cocoa",
                  "Merritt Island",
                  "West Melbourne",
                  "Indian Harbour Beach"
                ].map((city, idx) => (
                  <Link 
                    key={idx}
                    to={`/areas/${city.toLowerCase().replace(/ /g, '-')}`}
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {city}
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Contact CTA */}
          <Card className="bg-blue-50 border-2 border-blue-200">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Need Help Finding Something?
              </h3>
              <p className="text-gray-600 mb-6">
                Call us directly or send us a message. We're happy to help you find what you need.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+1-321-408-1347"
                  className="inline-flex items-center justify-center bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Call: 321-408-1347
                </a>
                <Link 
                  to="/contact"
                  className="inline-flex items-center justify-center border-2 border-blue-600 text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                >
                  Send a Message
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
};

export default NotFoundPage;
